import axios from 'axios';
import { dataURLToBlob } from './imageHelpers';

const UPLOAD = 'upload';
const UPDATE = 'update';
const DELETE = 'delete';
const CROP = 'crop';
const COPY = 'copy';

export { UPLOAD, UPDATE, DELETE, CROP, COPY };

/**
 * A new UploadManager is created like this:
 *     `const uploadManager = new UploadManager({ accessToken, entityId, entityType });`
 *
 * Upload an item by passing a task to addTask:
 *     `uploadManager.addTask(task)`
 *
 * where task is an object with this shape:
 *
 *     {
 *       type: oneOf [ UPLOAD, UPDATE, DELETE ]
 *       image: {
 *         source: dataURL,
 *         name: string,
 *         size: number,
 *         type: string,
 *         height: number,
 *         width: number,
 *         altText: string,
 *         caption: string,
 *         credit: string,
 *         type: oneof ['hero', 'preview', 'gallery'],
 *         position: number,
 *
 *         mediaId: string,    ** Updating metadata only,
 *         url: string,        ** Updating metadata only,
 *         title: string       ** Updating metadata only,
 *         description: string ** Updating metadata only,
 *         credits: string     ** Updating metadata only
 *      },
 *      onProgress: func,
 *      onCompletion: func,
 *      onError: func
 *    }
 *
 */

export default class UploadManager {
    constructor(attrs) {
        this.headers = {
            Authorization: `Bearer ${attrs.accessToken}`
        };
        this.entityId = attrs.entityId;
        this.entityType = attrs.entityType;

        this.queue = [];
    }

    addTask = (task) => {
        this.queue.push(task);
        this.drainQueue();
    };

    drainQueue = () => {
        const task = this.queue.shift();

        switch (task.type) {
            case UPLOAD:
                this.upload(task);
                break;
            case UPDATE:
                this.update(task);
                break;
            case DELETE:
                this.delete(task);
                break;
            case CROP:
                this.crop(task);
                break;
            case COPY:
                this.copy(task);
                break;
            default:
                this.upload(task);
        }
    };

    upload = (task) => {
        const image = task.image;
        const config = {
            headers: this.headers,
            onUploadProgress: task.onProgress
        };

        const imageFile = dataURLToBlob(image.source);
        imageFile.lastModifiedDate = new Date();
        imageFile.name = image.name;

        /* eslint-disable no-undef */
        const data = new FormData();
        /* eslint-enable no-undef */

        data.append('entity_id', this.entityId);
        data.append('entity_type', this.entityType);
        data.append('title', image.altText);
        data.append('description', image.caption);
        data.append('credits', image.credit);
        data.append('position', image.position);
        data.append('crop_x', image.pixelCrop.x);
        data.append('crop_y', image.pixelCrop.y);
        data.append('crop_width', image.pixelCrop.width);
        data.append('crop_height', image.pixelCrop.height);
        data.append('is_primary', image.type === 'hero' ? 'true' : 'false');
        data.append('is_preview', image.type === 'preview' ? 'true' : 'false');
        data.append('is_gallery', image.type === 'gallery' ? 'true' : 'false');
        data.append('file', imageFile, image.name);

        axios
            .post('/api/media/internal/upload', data, config)
            .then(task.onCompletion)
            .catch(task.onError);
    };

    delete = (task) => {
        const { image } = task;

        axios
            .delete(`/api/media/internal/${image.media_id}`, { headers: this.headers })
            .then(task.onCompletion)
            .catch(task.onError);
    };

    update = (task) => {
        const { image } = task;

        axios
            .post('/api/media/internal/save', image, { headers: this.headers })
            .then(task.onCompletion)
            .catch(task.onError);
    };

    crop = (task) => {
        const { image } = task;

        axios
            .post('/api/media/internal/crop', image, { headers: this.headers })
            .then(task.onCompletion)
            .catch(task.onError);
    };

    copy = (task) => {
        const { image } = task;

        axios
            .post('/api/media/internal/copy', image, { headers: this.headers })
            .then(task.onCompletion)
            .catch(task.onError);
    };
}
